import { useEffect, useState } from 'react';

import { useDashboardStore } from '@/state/store';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { FloorView } from '@/api/rest';

/**
 * Stable empty-array reference for the floors selector.  Returning a
 * fresh ``[]`` from a Zustand selector makes React's
 * ``useSyncExternalStore`` think the snapshot keeps changing and
 * aborts the render tree with error #185 (Maximum update depth
 * exceeded).
 */
const EMPTY_FLOORS: readonly FloorView[] = [];

/**
 * Top-3 leaderboard wall — appears on the building overview and
 * updates whenever a ``leaderboard.computed`` event flies through
 * (weekly task fires every Friday 15:50 UTC).  Falls back to a
 * placeholder before the first event arrives.
 */
export function Leaderboard() {
  const lb = useDashboardStore((s) => s.leaderboard);
  const podsByPodId = useDashboardStore(
    (s) => s.building?.floors ?? EMPTY_FLOORS,
  );
  const bp = useBreakpoint();
  // Drive a re-render ~every 1.5 s so the rank-change highlight fades
  // out after its 6 s window without needing per-row timers.  No-op
  // when no row has a recent change.
  const [, force] = useState(0);
  useEffect(() => {
    if (!lb) return;
    const anyRecent = lb.rows.some(
      (r) => r.changedAtMs > 0 && performance.now() - r.changedAtMs < 7_000,
    );
    if (!anyRecent) return;
    const id = window.setInterval(() => force((n) => n + 1), 1_500);
    return () => window.clearInterval(id);
  }, [lb]);
  // The board only carries content after the weekly leaderboard.computed
  // event lands (Fridays at 15:50 UTC).  On mobile, the rest of the
  // week it's an empty 25 vh box squeezing the building canvas -- hide
  // the empty state on phones so the canvas gets its space back.
  if (bp === 'mobile' && !lb) return null;

  // Build a lookup table from pod_id → display name for nicer rows.
  const nameByPodId = new Map<string, string>();
  for (const floor of podsByPodId) {
    for (const pod of floor.pods) nameByPodId.set(pod.pod_id, pod.name);
  }

  const top = (lb?.rows ?? []).slice(0, 3);

  return (
    <aside
      style={{
        background: '#0d1117',
        border: '1px solid #21262d',
        borderRadius: 12,
        padding: 14,
        minWidth: 240,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <strong style={{ fontSize: 13, letterSpacing: 0.5 }}>Leaderboard</strong>
        <span style={{ fontSize: 10, color: '#6e7681' }}>
          {lb ? lb.period : 'weekly'} · {lb?.metric ?? 'utility_avg'}
        </span>
      </header>
      {top.length === 0 ? (
        <div style={{ fontSize: 12, color: '#6e7681' }}>
          Awaiting Friday compute…
        </div>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {top.map((row) => {
            const accent = row.rank === 1 ? '#ffd33d' : row.rank === 2 ? '#9aa0a6' : '#cd7f32';
            const label = row.name ?? nameByPodId.get(row.pod_id) ?? row.pod_id;
            // Show the rank-change indicator for ~6 s after the live
            // ``leaderboard.rank_changed`` event lands.  Positive
            // delta = climbed (green ▲), negative = fell (red ▼).
            const showDelta =
              row.recentDelta !== 0 &&
              row.changedAtMs > 0 &&
              performance.now() - row.changedAtMs < 6_000;
            const deltaColor = row.recentDelta > 0 ? '#3fb950' : '#f85149';
            return (
              <li
                key={row.pod_id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '20px 1fr auto',
                  alignItems: 'center',
                  padding: '4px 0',
                  gap: 8,
                  fontSize: 12,
                  borderBottom: '1px solid #161b22',
                  background: showDelta ? `${deltaColor}11` : 'transparent',
                  transition: 'background 280ms ease-out',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    color: accent,
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  #{row.rank}
                </span>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {showDelta ? (
                    <span
                      aria-label={
                        row.recentDelta > 0
                          ? `climbed ${row.recentDelta} positions`
                          : `fell ${Math.abs(row.recentDelta)} positions`
                      }
                      style={{ color: deltaColor, fontSize: 10, fontWeight: 700 }}
                    >
                      {row.recentDelta > 0 ? '▲' : '▼'}
                      {Math.abs(row.recentDelta)}
                    </span>
                  ) : null}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {label}
                  </span>
                </span>
                <span style={{ color: row.value >= 0 ? '#2ea043' : '#f85149' }}>
                  {row.value >= 0 ? '+' : ''}
                  {row.value.toFixed(3)}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </aside>
  );
}
